import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Match a tour step route pattern against the current location.
 *
 * @param {import('react-router-dom').Location} location
 * @param {string|RegExp|undefined} pattern - Pathname or pathname?search pattern.
 * @returns {boolean}
 */
export const matchTourRoute = (location, pattern) => {
    if (!pattern) {
        return false;
    }

    const full = `${location.pathname}${location.search}`;

    if (pattern instanceof RegExp) {
        return pattern.test(full);
    }

    if (typeof pattern !== 'string') {
        return false;
    }

    if (pattern.includes('?')) {
        const [path, searchPart] = pattern.split('?');
        const pathnameMatch = location.pathname === path
            || location.pathname.startsWith(`${path}/`);
        if (!pathnameMatch) {
            return false;
        }
        return location.search.includes(searchPart);
    }

    return location.pathname === pattern
        || location.pathname.startsWith(`${pattern}/`);
};

/**
 * Hook: whether the current route satisfies a tour step's expected route.
 *
 * @param {string|RegExp|undefined} routePattern
 * @returns {boolean}
 */
export const useTourRouteMatch = (routePattern) => {
    const location = useLocation();

    return useMemo(
        () => matchTourRoute(location, routePattern),
        [location, routePattern],
    );
};

/**
 * Whether a tour step is complete per its completion rule.
 *
 * @param {object} step
 * @param {boolean} routeMatched
 * @param {boolean} markedDone
 * @param {boolean} skipped
 * @returns {boolean}
 */
export const isTourStepComplete = (step, routeMatched, markedDone, skipped) => {
    if (!step) {
        return false;
    }

    const mode = step.completion || 'manual';

    if (mode === 'route') {
        return routeMatched || markedDone || skipped;
    }

    if (mode === 'optional') {
        return markedDone || skipped || routeMatched;
    }

    return markedDone || skipped;
};

/**
 * Whether Next is enabled for the current step.
 *
 * @param {object} step
 * @param {boolean} routeMatched
 * @param {boolean} markedDone
 * @param {boolean} skipped
 * @returns {boolean}
 */
export const canAdvanceTourStep = (step, routeMatched, markedDone, skipped) => {
    if (!step) {
        return false;
    }

    if (step.id === 'welcome') {
        return true;
    }

    if (step.id === 'done') {
        return false;
    }

    return isTourStepComplete(step, routeMatched, markedDone, skipped);
};
